import { useAppDispatch, useAppSelector } from '@hooks/reduxHooks'
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  IconButton,
  Switch,
  SwitchProps,
  Tooltip,
  Typography,
} from '@mui/material'
import { DoctorDto } from '@services/api'
import { AddressDto, OrderDto, RecipientDTO, VisitDTO } from '@services/dtos'
import {} from '@state/reducers/orderReducer'
import { capitalize } from 'lodash'
import { AtSign, Mailbox, X } from 'lucide-react'
import { FC, useEffect, useMemo, useRef, useState } from 'react'
import DoctorList from '../../containers/app/parameters/doctors/DoctorList'
import {
  doAddRecipient,
  doGetRecipients,
  doRemoveRecipient,
  doUpdateRecipient,
} from '../../state/reducers/recipientReducer'
import ConfirmDialog from '../dialogs/ConfirmDialog'
import DialogTitle from '../dialogs/DialogTitle'
import DoctorForm from '../forms/DoctorForm'
import { enqueueSnackbar } from '@state/reducers/alertReducer'

const TinySwitch: FC<SwitchProps> = (props) => (
  <div className="scale-75 origin-left">
    <Switch
      size="small"
      {...props}
      sx={{
        '& .MuiSwitch-switchBase': {
          padding: '1px',
          '&.Mui-checked': {
            color: '#4AD66D !important',
            '& + .MuiSwitch-track': {
              backgroundColor: '#8DF1B3 !important',
              opacity: 0.9,
              boxShadow: '0 0 8px rgba(141, 241, 179, 0.6)',
            },
          },
        },
        '& .MuiSwitch-thumb': {
          width: 18,
          height: 18,
          marginTop: '2px',
        },
        '& .MuiSwitch-track': {
          borderRadius: 7,
          transition: 'all 0.3s ease',
        },
        width: 40,
        height: 24,
        ...props.sx,
      }}
    />
  </div>
)

const RenderList: FC<{
  recipients: RecipientDTO[]
  onDelete: (id: number) => void
  onSelectAddress: (recipient: RecipientDTO) => void
  onSelectEmail: (recipient: RecipientDTO) => void
  onReceiveMethodChange: (recipient: RecipientDTO) => void
  doctorRoleMap?: { [key: string]: string }
}> = ({
  recipients,
  onDelete,
  onSelectAddress,
  onReceiveMethodChange,
  doctorRoleMap,
}) => {
  return (
    <div className="flex flex-col gap-2">
      {recipients.map((rc) => (
        <div
          key={rc.id}
          className="flex flex-col gap-1 border border-gray-300 rounded-lg bg-gray-50 p-1 sm:p-2"
        >
          <div className="flex items-start sm:justify-between gap-1">
            <div className="flex items-center gap-2">
              {rc.doctor && (
                <div className="text-xs sm:text-sm space-x-1">
                  <span className="font-medium">
                    {capitalize(rc.doctor.firstName)}{' '}
                    {capitalize(rc.doctor.lastName)}
                  </span>
                  {doctorRoleMap && doctorRoleMap[rc.id] && (
                    <span className="text-[10px] sm:text-xs bg-blue-100 text-blue-800 px-1 sm:px-2 py-0.5 rounded">
                      {doctorRoleMap[rc.id]}
                    </span>
                  )}
                  {!!rc.doctor.skill.trim() && (
                    <span className="text-[10px] sm:text-xs">
                      ({rc.doctor.skill})
                    </span>
                  )}
                  <span className="text-[10px] sm:text-xs text-gray-600 ml-1 sm:ml-2 truncate">
                    {rc.doctor.email}
                  </span>
                </div>
              )}
            </div>
            <IconButton
              type="button"
              color="error"
              className="z-10"
              onClick={() => onDelete(rc.id)}
            >
              <X size={16} />
            </IconButton>
          </div>

          <div className="flex items-center justify-end gap-1 md:gap-2 2xl:gap-3">
            <div className="flex items-center">
              <Tooltip title="Recevoir par email">
                <TinySwitch
                  checked={rc.receiveByEmail}
                  onChange={(_, checked) =>
                    onReceiveMethodChange({
                      ...rc,
                      receiveByEmail: checked,
                    })
                  }
                  color="primary"
                />
              </Tooltip>
              <AtSign
                size={14}
                className="sm:w-[18px] sm:h-[18px] text-black"
              />
            </div>

            <div className="flex items-center">
              <Tooltip title="Recevoir par courrier">
                <TinySwitch
                  checked={rc.receiveByPost}
                  onChange={(_, checked) =>
                    onReceiveMethodChange({
                      ...rc,
                      receiveByPost: checked,
                    })
                  }
                  color="primary"
                />
              </Tooltip>
              <Mailbox
                size={14}
                className="sm:w-[18px] sm:h-[18px] text-black"
              />
            </div>

            {rc.selectedAddress && (
              <Tooltip
                title={`${rc.selectedAddress.locationName}, ${rc.selectedAddress.line1}, ${rc.selectedAddress.postalCode} ${rc.selectedAddress.city}`}
              >
                <div
                  className="flex items-center gap-1 text-[10px] sm:text-xs border-l pl-1 sm:pl-2 cursor-pointer hover:bg-gray-200 rounded px-1 sm:px-2 py-1 transition-colors"
                  onClick={() => onSelectAddress(rc)}
                >
                  <i className="fas fa-map-marker-alt text-gray-500 text-[10px] sm:text-xs"></i>
                  <span className="truncate max-w-[80px] sm:max-w-[150px]">
                    {rc.selectedAddress.locationName.length > 6
                      ? `${rc.selectedAddress.locationName.substring(0, 6)}...`
                      : rc.selectedAddress.locationName}
                  </span>
                </div>
              </Tooltip>
            )}

            {rc.doctor && !rc.selectedAddressId && rc.receiveByPost && (
              <Tooltip title="Aucune adresse sélectionnée">
                <Button
                  size="small"
                  variant="outlined"
                  color="warning"
                  onClick={() => onSelectAddress(rc)}
                  className="text-xs py-0 px-1 min-w-0"
                >
                  <i className="fas fa-exclamation-triangle mr-1"></i>
                  Adresse
                </Button>
              </Tooltip>
            )}
          </div>
        </div>
      ))}
    </div>
  )
}

type Props = { order?: OrderDto; visit?: VisitDTO }

const OrderRecipientList: FC<Props> = ({ order, visit }) => {
  const dispatch = useAppDispatch()
  const [isAddOpen, setIsAddOpen] = useState(false)
  const [selectingRecipientAddress, setSelectingRecipientAddress] =
    useState<RecipientDTO | null>(null)
  const [deletingRecipient, setDeletingRecipient] = useState<number | null>(
    null,
  )
  const [editingDoctor, setEditingDoctor] = useState<DoctorDto | null>(null)

  const { recipients } = useAppSelector(({ recipient }) => ({
    recipients: recipient.recipients,
  }))

  const orderId = order?.id
  const visitId = visit?.id
  const isMountedRef = useRef(true)

  useEffect(() => {
    return () => {
      isMountedRef.current = false
    }
  }, [])

  useEffect(() => {
    if (!orderId && !visitId) {
      return
    }
    dispatch(doGetRecipients({ orderId, visitId }))
  }, [dispatch, orderId, visitId])

  useEffect(() => {
    if (selectingRecipientAddress) {
      const selectedRecipient = recipients.find(
        (r) => r.id === selectingRecipientAddress?.id,
      )
      if (selectedRecipient) {
        setSelectingRecipientAddress(selectedRecipient)
      }
    }
  }, [recipients])

  const doctorRoleMap = useMemo<{ [key: string]: string }>(() => {
    if (!order) return {}
    const map = {}
    recipients.forEach((recipient) => {
      if (recipient.doctor) {
        if (recipient.doctor.id === order.referringDoctor?.id) {
          map[recipient.id] = 'Prescripteur'
        } else if (recipient.doctor.id === order.patient.familyDoctor?.id) {
          map[recipient.id] = 'Traitant'
        }
      } else if (recipient.member) {
        if (recipient.member.id === order.attendingDoctor?.id) {
          map[recipient.id] = 'Traitant'
        } else if (recipient.member.id === order.consultingDoctor?.id) {
          map[recipient.id] = 'Consultant'
        }
      }
    })
    return map
  }, [recipients, order])

  const handleDoctorSelected = async (doctor: DoctorDto[]) => {
    const newRecipients: DoctorDto[] = []
    for (const d of doctor) {
      if (!recipients.find((rp) => rp.doctor?.id === d.id)) {
        newRecipients.push(d)
      }
    }
    for (const d of newRecipients) {
      await dispatch(
        doAddRecipient({
          doctorId: d.id,
          orderId,
          visitId,
          ...(d.addresses?.length === 1 && {
            selectedAddressId: d.addresses[0].id,
          }),
        }),
      )

      if (isMountedRef.current) {
        dispatch(
          enqueueSnackbar({
            message: 'Destinataire sélectionné',
            type: 'success',
          }),
        )
      }
    }
  }

  const onDeleteRecipient = async () => {
    if (deletingRecipient) {
      await dispatch(doRemoveRecipient(deletingRecipient)).unwrap()

      if (isMountedRef.current) {
        setDeletingRecipient(null)
      }

      if (isMountedRef.current) {
        dispatch(
          enqueueSnackbar({
            message: 'Destinataire desélectionné',
            type: 'success',
          }),
        )
      }
    }
  }

  const handleSelectAddress = async (address: AddressDto) => {
    if (selectingRecipientAddress) {
      await dispatch(
        doUpdateRecipient({
          id: selectingRecipientAddress.id,
          dto: { selectedAddressId: address.id },
        }),
      ).unwrap()

      if (isMountedRef.current) {
        setSelectingRecipientAddress(null)
      }
    }
  }

  const onEditDoctor = (doctor: DoctorDto) => {
    setEditingDoctor(doctor)
  }

  const handleReceptionMethodChange = (recipient: RecipientDTO) => {
    dispatch(
      doUpdateRecipient({
        id: recipient.id,
        dto: {
          receiveByEmail: recipient.receiveByEmail,
          receiveByPost: recipient.receiveByPost,
        },
      }),
    )
  }

  const handleSelectEmail = (recipient: RecipientDTO) => {
    console.log(recipient)
  }

  return (
    <>
      <Box className="flex gap-2 justify-between">
        <Typography variant="h6">Destinataires</Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={() => setIsAddOpen(true)}
          type="button"
          size="small"
          sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
        >
          <i className="fas fa-plus text-xs sm:text-sm"></i>
          <span className="ml-1">Ajouter un destinataire</span>
        </Button>
      </Box>
      <RenderList
        recipients={recipients}
        onDelete={setDeletingRecipient}
        onSelectAddress={setSelectingRecipientAddress}
        onSelectEmail={handleSelectEmail}
        onReceiveMethodChange={handleReceptionMethodChange}
        doctorRoleMap={doctorRoleMap}
      />

      <Dialog
        fullWidth
        maxWidth="xl"
        open={isAddOpen}
        onClose={() => setIsAddOpen(false)}
      >
        <DialogTitle
          title="Sélectionner un ou plusieurs médecins"
          onClose={() => setIsAddOpen(false)}
        />
        <DialogContent
          className="p-0 flex h-full gap-2"
          sx={{
            height: {
              xs: 'calc(100vh - 120px)',
              sm: 'calc(100vh - 150px)',
              md: 'calc(100vh - 200px)',
            },
          }}
        >
          <div className="flex-1">
            <DoctorList
              onSelectionChange={handleDoctorSelected}
              selectedRows={recipients.reduce((acc, r) => {
                if (r.doctor) {
                  acc.push(r.doctor)
                }
                return acc
              }, new Array<DoctorDto>())}
            />
          </div>
          <div className="flex flex-col gap-2 w-full sm:w-1/4 p-1 sm:p-3 border rounded-lg">
            <h3 className="text-base sm:text-lg font-semibold">
              Destinataires selectionnés
            </h3>
            <RenderList
              recipients={recipients}
              onDelete={setDeletingRecipient}
              onSelectAddress={setSelectingRecipientAddress}
              onReceiveMethodChange={handleReceptionMethodChange}
              onSelectEmail={() => {}}
              doctorRoleMap={doctorRoleMap}
            />
          </div>
        </DialogContent>
      </Dialog>
      <Dialog
        open={!!selectingRecipientAddress}
        onClose={() => setSelectingRecipientAddress(null)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle
          title="Sélectionner une adresse"
          onClose={() => setSelectingRecipientAddress(null)}
        />
        <DialogContent>
          <div className="p-2">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-4">
              {selectingRecipientAddress?.doctor?.addresses?.map((a) => (
                <div
                  key={a.id}
                  className={`p-3 bg-white rounded-lg border transition-all hover:shadow-md ${
                    selectingRecipientAddress.selectedAddressId === a.id
                      ? 'border-blue-500 ring-2 ring-blue-100'
                      : 'border-gray-200 hover:border-blue-300'
                  } cursor-pointer flex flex-col h-full`}
                  onClick={() => handleSelectAddress(a)}
                >
                  <div className="flex items-start justify-between">
                    <div className="font-medium text-gray-900 truncate">
                      {a.locationName}
                    </div>
                    {selectingRecipientAddress.selectedAddressId === a.id && (
                      <div className="bg-blue-500 text-white rounded-full p-1 -mt-1 -mr-1">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-4 w-4"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fillRule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </div>
                    )}
                  </div>
                  <div className="text-sm text-gray-600 mt-1 flex-grow">
                    <div className="truncate">{a.line1}</div>
                    {a.line2 && <div className="truncate">{a.line2}</div>}
                    <div className="truncate">
                      {a.postalCode} {a.city}
                    </div>
                  </div>
                  {a.finessNumber && (
                    <div className="text-xs text-gray-500 mt-2 border-t pt-1 border-gray-100">
                      N° FINESS: {a.finessNumber}
                    </div>
                  )}
                </div>
              ))}
              {selectingRecipientAddress?.doctor?.addresses?.length === 0 && (
                <div className="col-span-2 p-4 bg-white rounded-lg border border-gray-200 flex items-center justify-between">
                  <div className="text-sm text-gray-600">
                    Aucune adresse disponible pour ce médecin
                  </div>
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    startIcon={
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-4 w-4"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z"
                          clipRule="evenodd"
                        />
                      </svg>
                    }
                    onClick={() => {
                      if (selectingRecipientAddress.doctor) {
                        onEditDoctor(selectingRecipientAddress.doctor)
                      }
                    }}
                  >
                    Ajouter une adresse
                  </Button>
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog
        open={!!editingDoctor}
        onClose={() => setEditingDoctor(null)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle
          title="Modifier le médecin"
          onClose={() => setEditingDoctor(null)}
        />
        <DialogContent>
          <DoctorForm
            editingDoctor={editingDoctor}
            onClose={() => setEditingDoctor(null)}
          />
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!deletingRecipient}
        severity="error"
        title="Suppression d'un destinataire"
        message="Voulez-vous vraiment supprimer ce destinataire ?"
        onCancel={() => setDeletingRecipient(null)}
        onConfirm={onDeleteRecipient}
      />
    </>
  )
}

export default OrderRecipientList
